import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericPost } from '@/_shared/types';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_USER_HISTORY_PATH_KEY } from '@/swagger/constants/credits-keys';
import { API_POST_EMPLOYER_CREDITS_HISTORY, KEY_POST_EMPLOYER_CREDITS_HISTORY } from '../constants';
import { MOCK_RESPONSE_POST_EMPLOYER_CREDITS_HISTORY } from '../mocks';

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsPostEmployerCreditsHistory, 'successSaga' | 'errorSaga'>;
};

type RequestPostEmployerCreditsHistory = GenericPost<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_USER_HISTORY_PATH_KEY
>['request']['body'];

type ResponsePostEmployerCreditsHistory = GenericPost<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_USER_HISTORY_PATH_KEY
>['response']['body'];

export type CallParamsPostEmployerCreditsHistory = RequestPostEmployerCreditsHistory & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponsePostEmployerCreditsHistory = ResponsePostEmployerCreditsHistory;

type CallErrorPostEmployerCreditsHistory = {
  message: string;
  code: number;
};

type CallSelectorsPostEmployerCreditsHistory = {
  data: ResponsePostEmployerCreditsHistory['data'];
  totalItems: ResponsePostEmployerCreditsHistory['totalItems'];
  fullRequest: FullRequestType;
};

let internalPayload = {};

export const postEmployerCreditsHistoryCall = createGeneralCall<
  CallParamsPostEmployerCreditsHistory,
  CallResponsePostEmployerCreditsHistory,
  CallErrorPostEmployerCreditsHistory,
  CallSelectorsPostEmployerCreditsHistory
>({
  id: KEY_POST_EMPLOYER_CREDITS_HISTORY,
  url: () => state => {
    const org = getCurrentOrganization(state);
    return API_POST_EMPLOYER_CREDITS_HISTORY(org as unknown as string);
  },
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  method: 'post',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_POST_EMPLOYER_CREDITS_HISTORY : undefined,
  selectors: {
    data: ({ selectors }) => createSelector(selectors.response, res => res?.data || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems || 0),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
});
